import React, { useState } from 'react';
import { Routes, Route, useNavigate } from 'react-router-dom';
import OrderList from '@/components/admin/OrderList';
import RouteCalculator from '@/components/admin/RouteCalculator';
import { OrderTest } from '@/components/admin/OrderTest';
import { supabase } from '@/lib/supabase';
import { MapPin, Printer, X } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { fetchDeliveryStartDate, calculateAvailableDeliveryDates, fetchDeliverySlots } from '@/lib/deliveryUtils';

export interface OrderForDelivery {
  id: string;
  delivery_date: string;
  status?: string;
  profiles?: {
    id: string;
    first_name: string;
    last_name: string;
    email: string;
    phone: string | null;
    address: string;
  };
}

export function Orders() {
  const [showRouteCalculator, setShowRouteCalculator] = useState(false);
  const [showDeliveryNotePopup, setShowDeliveryNotePopup] = useState(false);
  const [orders, setOrders] = useState<OrderForDelivery[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [nextDeliveryDate, setNextDeliveryDate] = useState<string | null>(null);
  const [availableDeliveryDates, setAvailableDeliveryDates] = useState<string[]>([]);
  const [selectedDeliveryDate, setSelectedDeliveryDate] = useState<string>('');
  const [selectedRouteDate, setSelectedRouteDate] = useState<string>('');
  const navigate = useNavigate();
  const { t } = useTranslation();

  // Load next delivery date when component mounts
  React.useEffect(() => {
    async function loadNextDeliveryDate() {
      try {
        const startDate = await fetchDeliveryStartDate();
        const slots = await fetchDeliverySlots();
        const availableDates = calculateAvailableDeliveryDates(startDate, slots);
        
        if (availableDates.length > 0) {
          // Format the next delivery date as YYYY-MM-DD
          const nextDate = availableDates[0].date.toISOString().split('T')[0];
          console.log('Next delivery date:', nextDate);
          console.log('Available slots:', availableDates[0].slots);
          setNextDeliveryDate(nextDate);

          // Load all upcoming delivery dates that have orders
          loadUpcomingDeliveryDates();
        } else {
          console.log('No available delivery dates found');
        }
      } catch (err) {
        console.error('Error loading next delivery date:', err);
      }
    }
    
    loadNextDeliveryDate();
  }, []);

  // Load orders for the route calculator
  React.useEffect(() => {
    if (showRouteCalculator) {
      console.log('Route calculator shown, fetching orders...');
      fetchOrdersForDelivery();
    }
  }, [showRouteCalculator, selectedRouteDate]);

  // Add an effect to reload delivery dates when popup is opened
  React.useEffect(() => {
    if (showDeliveryNotePopup) {
      loadUpcomingDeliveryDates();
    }
  }, [showDeliveryNotePopup]);

  const loadUpcomingDeliveryDates = async () => {
    try {
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      const todayStr = today.toISOString().split('T')[0];
      
      // Define the expected type from the database
      interface OrderDateResponse {
        delivery_date: string;
      }
      
      // Fetch all distinct delivery dates from today onwards
      // Don't filter by status as we want all orders with delivery dates
      const { data, error } = await supabase
        .from('orders')
        .select('delivery_date')
        .gte('delivery_date', todayStr)
        .order('delivery_date')
        .not('delivery_date', 'is', null);
      
      if (error) throw error;
      
      if (data) {
        console.log('Delivery dates found:', data);
        // Extract unique delivery dates with proper typing
        const typedData = data as OrderDateResponse[];
        const uniqueDates = [...new Set(typedData.map(order => order.delivery_date))];
        console.log('Unique delivery dates:', uniqueDates);
        setAvailableDeliveryDates(uniqueDates);
        
        // Set the first date as selected by default if available
        if (uniqueDates.length > 0) {
          setSelectedDeliveryDate(uniqueDates[0]);
        }
      }
    } catch (err) {
      console.error('Error loading delivery dates:', err);
    }
  };

  const fetchOrdersForDelivery = async () => {
    try {
      setIsLoading(true);
      console.log('Starting to fetch orders...');
      
      // Get today's date in YYYY-MM-DD format
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      const todayStr = today.toISOString().split('T')[0];
      
      // Use today's date or the selected date
      const queryDate = selectedRouteDate || todayStr;
      console.log('Fetching orders from date:', queryDate);
      
      // Use a single query with nested select to avoid multiple requests
      const query = supabase
        .from('orders')
        .select(`
          id, 
          delivery_date,
          status,
          profiles:profiles(
            id,
            first_name, 
            last_name, 
            email, 
            phone, 
            address
          ),
          address,
          guest_email,
          guest_details
        `);
      
      // If a specific date is selected, filter by that date
      // Otherwise fetch all orders from today onward
      if (selectedRouteDate) {
        query.eq('delivery_date', selectedRouteDate);
      } else {
        query.gte('delivery_date', todayStr);
      }
      
      const { data, error } = await query.order('delivery_date', { ascending: true });

      console.log('Raw orders data:', data);
      console.log('Query error:', error);

      if (error) throw error;
      
      if (data && data.length > 0) {
        // Transform the API response to match our interface
        const formattedOrders: OrderForDelivery[] = data.map((order) => {
          let profileData: OrderForDelivery['profiles'] = undefined;
          
          if (order.profiles) {
            // Use profiles data if available
            profileData = Array.isArray(order.profiles) ? order.profiles[0] : order.profiles;
          } else if (order.guest_details) {
            // Handle guest orders
            let guestDetails = order.guest_details;
            if (typeof guestDetails === 'string') {
              try {
                guestDetails = JSON.parse(guestDetails);
              } catch (e) {
                console.error('Error parsing guest details:', e);
                guestDetails = {};
              }
            }
            
            profileData = {
              id: 'guest',
              first_name: String(guestDetails.firstName || 'Guest'),
              last_name: String(guestDetails.lastName || 'User'),
              email: String(order.guest_email || ''),
              phone: guestDetails.phone ? String(guestDetails.phone) : null,
              address: String(order.address || '')
            };
          }
          
          return {
            id: order.id,
            delivery_date: order.delivery_date,
            status: order.status,
            profiles: profileData
          };
        });
        
        // Filter out orders without addresses
        const ordersWithAddresses = formattedOrders.filter(
          order => order.profiles && order.profiles.address
        );
        
        console.log('Formatted orders with addresses:', ordersWithAddresses);
        console.log('Number of orders with addresses:', ordersWithAddresses.length);
        setOrders(ordersWithAddresses);
      } else {
        console.log('No orders found');
        setOrders([]);
      }
    } catch (err) {
      console.error('Error fetching orders for delivery:', err);
    } finally {
      setIsLoading(false);
    }
  };

  const handlePrintDeliveryNotes = () => {
    if (!selectedDeliveryDate) return;

    // Create a new window for printing
    const printWindow = window.open('', '_blank');
    if (!printWindow) {
      alert('Tillåt popup-fönster för att skriva ut följesedlar');
      return;
    }

    // Fetch orders for the selected delivery date
    const fetchOrdersAndPrint = async () => {
      try {
        const { data, error } = await supabase
          .from('orders')
          .select(`
            id, 
            display_id,
            created_at,
            delivery_date,
            status,
            guest_email,
            guest_details,
            address,
            delivery_notes,
            order_items (
              id,
              quantity,
              product:products (
                id,
                name,
                price
              )
            ),
            profiles:profiles(
              id,
              first_name, 
              last_name, 
              email, 
              phone, 
              address
            )
          `)
          .eq('delivery_date', selectedDeliveryDate);

        if (error) throw error;

        if (data && printWindow) {
          // Generate HTML content for printing
          let htmlContent = `
            <!DOCTYPE html>
            <html>
            <head>
              <title>Följesedlar - ${selectedDeliveryDate}</title>
              <style>
                body { font-family: Arial, sans-serif; margin: 0; padding: 20px; }
                .delivery-note { page-break-after: always; padding: 20px; border-bottom: 1px dashed #ccc; }
                .delivery-note:last-child { page-break-after: avoid; }
                h1 { font-size: 18px; margin-bottom: 10px; }
                h2 { font-size: 16px; margin: 15px 0 5px; }
                table { width: 100%; border-collapse: collapse; margin: 10px 0; }
                table, th, td { border: 1px solid #ddd; }
                th, td { padding: 8px; text-align: left; }
                .customer-info { margin-bottom: 20px; }
                .order-info { margin-bottom: 20px; padding: 10px; background-color: #f9f9f9; border-radius: 4px; }
                .footer { margin-top: 20px; font-size: 12px; }
              </style>
            </head>
            <body>
          `;

          // Add each order as a separate delivery note
          data.forEach(order => {
            let customerName, customerPhone, customerEmail;
            
            // Parse guest details if it's a string
            let guestDetails = order.guest_details;
            if (typeof guestDetails === 'string') {
              try {
                guestDetails = JSON.parse(guestDetails);
              } catch (e) {
                console.error('Error parsing guest details:', e);
                guestDetails = {};
              }
            }
            
            // For debugging
            console.log('Order details:', { 
              orderId: order.id, 
              address: order.address,
              guest_details: guestDetails,
              guest_email: order.guest_email
            });
            
            if (order.profiles) {
              // Logged-in user
              customerName = `${order.profiles.first_name} ${order.profiles.last_name}`;
              customerPhone = order.profiles.phone || 'Inget telefonnummer';
              customerEmail = order.profiles.email || 'Ingen e-postadress';
            } else {
              // Guest user
              customerName = guestDetails && (guestDetails.firstName || guestDetails.lastName)
                ? `${guestDetails.firstName || ''} ${guestDetails.lastName || ''}`.trim()
                : 'Gästkund';
              customerPhone = guestDetails?.phone || '';
              customerEmail = order.guest_email || 'Ingen e-postadress';
            }
            
            // Log phone numbers for debugging
            console.log('Phone number:', { 
              orderId: order.id, 
              rawPhone: guestDetails?.phone,
              customerPhone
            });
            
            // Use the address directly from the order - this is the delivery address
            const customerAddress = order.address || 'Ingen adress';
            
            // Format created date in Swedish format
            const createdDate = order.created_at 
              ? new Date(order.created_at).toLocaleDateString('sv-SE')
              : 'Okänt datum';
            
            htmlContent += `
              <div class="delivery-note">
                <h1>Följesedel - Order #${order.display_id || order.id}</h1>
                
                <div class="order-info">
                  <p><strong>Ordernr:</strong> ${order.display_id || order.id}</p>
                  <p><strong>Skapad:</strong> ${createdDate}</p>
                  <p><strong>Leveransdatum:</strong> ${order.delivery_date}</p>
                </div>
                
                <div class="customer-info">
                  <h2>Kunduppgifter</h2>
                  <p><strong>Namn:</strong> ${customerName}</p>
                  <p><strong>Adress:</strong> ${(customerAddress || 'Ingen adress angiven').replace(/\n/g, '<br>')}</p>
                  <p><strong>Telefon:</strong> ${customerPhone || 'Inget telefonnummer'}</p>
                  <p><strong>E-post:</strong> ${customerEmail}</p>
                </div>
                
                <h2>Beställning</h2>
                <table>
                  <thead>
                    <tr>
                      <th>Produkt</th>
                      <th>Antal</th>
                    </tr>
                  </thead>
                  <tbody>
            `;
            
            // Add order items
            if (order.order_items && order.order_items.length > 0) {
              order.order_items.forEach(item => {
                htmlContent += `
                  <tr>
                    <td>${item.product?.name || 'Okänd produkt'}</td>
                    <td>${item.quantity}</td>
                  </tr>
                `;
              });
            } else {
              htmlContent += `
                <tr>
                  <td colspan="2">Inga produkter i denna order</td>
                </tr>
              `;
            }
            
            htmlContent += `
                  </tbody>
                </table>
                
                <div style="margin-top: 30px;">
                  <h2>Leveransinstruktioner</h2>
                  <div style="border: 1px solid #ddd; padding: 10px; min-height: 80px; border-radius: 4px;">
                    ${(order.delivery_notes || 'Ingen leveransinstruktion').replace(/\n/g, '<br>')}
                  </div>
                </div>
                
                <div class="footer">
                  <p>Utskriven den: ${new Date().toLocaleDateString('sv-SE')}</p>
                </div>
              </div>
            `;
          });
          
          htmlContent += `
            </body>
            </html>
          `;
          
          // Write to the new window and print
          printWindow.document.open();
          printWindow.document.write(htmlContent);
          printWindow.document.close();
          
          // Wait for content to load then print
          printWindow.onload = function() {
            printWindow.print();
            // Don't close the window to allow manual printing
          };
        } else {
          if (printWindow) {
            printWindow.document.write('<p>Inga ordrar hittades för det valda datumet.</p>');
            printWindow.document.close();
          }
        }
      } catch (err) {
        console.error('Error fetching orders for printing:', err);
        if (printWindow) {
          printWindow.document.write('<p>Fel vid laddning av ordrar. Vänligen försök igen.</p>');
          printWindow.document.close();
        }
      }
    };

    fetchOrdersAndPrint();
  };

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h1 className="text-2xl font-bold text-gray-800">Beställningar</h1>
        <div className="flex space-x-3">
          <button
            onClick={() => setShowDeliveryNotePopup(true)}
            className="px-4 py-2 text-white bg-blue-600 hover:bg-blue-700 rounded-md flex items-center transition-colors"
          >
            <Printer size={16} className="mr-2" />
            Följesedel
          </button>
          <button
            onClick={() => setShowRouteCalculator(!showRouteCalculator)}
            className={`px-4 py-2 text-white rounded-md flex items-center transition-colors ${
              showRouteCalculator ? 'bg-gray-600 hover:bg-gray-700' : 'bg-green-600 hover:bg-green-700'
            }`}
            disabled={isLoading}
          >
            <MapPin size={16} className="mr-2" />
            {showRouteCalculator ? "Dölj ruttplanerare" : "Ruttplanerare"}
          </button>
        </div>
      </div>

      {isLoading && (
        <div className="w-full py-8 flex justify-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-green-500"></div>
        </div>
      )}

      {showRouteCalculator ? (
        <div className="bg-white p-6 rounded-lg shadow-md border border-gray-100">
          <h2 className="text-lg font-medium mb-4 text-gray-800">Leveransruttplanerare</h2>
          
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Leveransdatum
            </label>
            <select
              value={selectedRouteDate}
              onChange={(e) => {
                setSelectedRouteDate(e.target.value);
              }}
              className="w-full sm:w-64 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="">Alla kommande leveranser</option>
              {availableDeliveryDates.length > 0 ? (
                availableDeliveryDates.map(date => (
                  <option key={date} value={date}>
                    {date}
                  </option>
                ))
              ) : (
                <option value="" disabled>Inga kommande leveransdatum</option>
              )}
            </select>
          </div>
          
          <RouteCalculator orders={orders} />
        </div>
      ) : (
        <div className="bg-white rounded-lg shadow-md border border-gray-100 overflow-hidden">
          <OrderList />
        </div>
      )}

      {/* Delivery Note Popup */}
      {showDeliveryNotePopup && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-xl font-semibold">Skriv ut följesedlar</h2>
              <button 
                onClick={() => setShowDeliveryNotePopup(false)}
                className="text-gray-500 hover:text-gray-700"
              >
                <X size={20} />
              </button>
            </div>
            
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Leveransdatum
              </label>
              <select
                value={selectedDeliveryDate}
                onChange={(e) => setSelectedDeliveryDate(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                {availableDeliveryDates.length > 0 ? (
                  availableDeliveryDates.map(date => (
                    <option key={date} value={date}>
                      {date}
                    </option>
                  ))
                ) : (
                  <option value="">Inga kommande leveransdatum</option>
                )}
              </select>
            </div>
            
            <div className="flex justify-end mt-6">
              <button
                onClick={() => setShowDeliveryNotePopup(false)}
                className="px-4 py-2 text-gray-600 mr-2"
              >
                Avbryt
              </button>
              <button
                onClick={handlePrintDeliveryNotes}
                disabled={!selectedDeliveryDate}
                className={`px-4 py-2 text-white rounded-md flex items-center ${
                  selectedDeliveryDate ? 'bg-blue-600 hover:bg-blue-700' : 'bg-gray-400'
                }`}
              >
                <Printer size={16} className="mr-2" />
                Skriv ut
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}